import type { HelpArticle } from "../../types";
import { MAPLE_PROJECT } from "../../../calculations/calcPreviewFixtures";

export const designMethodArticle: HelpArticle = {
  id: "design-method",
  title: "ASD and LRFD",
  description:
    "How the project default and sheet overrides resolve, what the project save recomputes, and where current schema-sheet controls remain incomplete.",
  tags: ["ASD", "LRFD", "project default", "override", "recompute", "provenance"],
  lastUpdated: "July 20, 2026",
  published: false,
  blocks: [
    {
      kind: "lead",
      text: "Firmark stores an ASD or LRFD project default and, on supported sheets, an optional sheet override. The effective method routes the calculation through the corresponding engine path and is recorded with the returned result. The control records a design-basis decision; it does not make that decision for the engineer.",
    },

    { kind: "heading", level: 2, text: "The project default", id: "project-default" },
    { kind: "where", path: ["Calculations", MAPLE_PROJECT.name, "Report details"] },
    {
      kind: "paragraph",
      text: "Report details stores the calculation project's default method. ASD and LRFD are the available project values. The same card also shows the fixed NDS-2024 engine standard, fixed ASCE 7-22 load standard, building code, risk category, and project deflection defaults. Changing the project method does not rewrite those other criteria.",
    },
    {
      kind: "shot",
      shotId: "design-method-project-default",
      caption: "The project design method beside the fixed engine and load standards and the report code basis.",
    },
    {
      kind: "paragraph",
      text: "The method takes effect when Report details is saved. The API stores the new project default and recomputes every non-load sheet in the project's load-link graph, in topological order. Sources run before targets, so an inherited reaction is resolved from the newly computed upstream record rather than an older packet.",
    },
    {
      kind: "paragraph",
      text: "A sheet with no override inherits the new project method. A sheet with an explicit ASD or LRFD override keeps that method, but it is still recomputed: an upstream reaction may have changed even when the downstream sheet's own method did not. Load-calculation sheets are not recomputed. They publish nominal, unfactored actions, so a project ASD/LRFD change belongs in downstream combinations and checks, not in the load generator.",
    },
    {
      kind: "callout",
      variant: "note",
      title: "One project save can replace many records",
      body: "The response returns the project sheet list after the graph run. Review recomputed member and design sheets as new results; do not assume that only inheriting sheets changed merely because explicit overrides were preserved.",
    },

    { kind: "heading", level: 2, text: "Wood member overrides", id: "sheet-overrides" },
    { kind: "where", path: ["Calculations", MAPLE_PROJECT.name, "FJ-1", "Design conditions"] },
    {
      kind: "paragraph",
      text: "A wood member's Design method control offers **Project default**, **ASD override**, and **LRFD override**. Project default stores a null override and resolves the effective method from the project when the sheet computes. Either explicit choice is stored in the calculation's dedicated override field, separate from ordinary input JSON, so editing geometry or loads does not erase it.",
    },
    {
      kind: "shot",
      shotId: "design-method-sheet-inherited",
      caption: "FJ-1 with a null override: Project default names the effective project method in the choice itself.",
    },
    { kind: "registry-field", calculatorId: "wood_beam_asd", fieldKey: "design_method" },
    {
      kind: "paragraph",
      text: "Changing the member choice commits immediately. The update request carries the override, the server injects the effective method into the engine inputs, and the returned calculation replaces the prior result. If that member publishes reactions, the API can mark linked downstream sheets out of date because the source packet may have changed. Recompute all is the separate action that settles those downstream sheets in graph order.",
    },

    { kind: "heading", level: 2, text: "LRFD exposes its duration input", id: "lrfd-duration" },
    {
      kind: "paragraph",
      text: "On a wood member, an effective LRFD choice reveals **Live-load duration** in Design conditions. The choice is an LRFD input used by the supported engine path; the ASD view does not show it. Switching back to Project default can hide or show it according to the project's effective method. The returned record remains the source of truth for the method and factors actually used.",
    },
    {
      kind: "shot",
      shotId: "design-method-sheet-lrfd",
      caption: "An explicit LRFD member override with the live-load duration control visible in the same Design conditions card.",
    },
    { kind: "registry-field", calculatorId: "wood_beam_asd", fieldKey: "live_duration_category" },
    {
      kind: "paragraph",
      text: "ASD and LRFD are not a label swap over one utilization. They can use different combinations, resistance or adjustment paths, and supporting inputs. Compare the method, governing combination, demand, capacity, and factor trace on the replacement record. A similar percentage by itself does not prove that two method runs followed the same path.",
    },

    { kind: "heading", level: 2, text: "Schema-sheet limitation", id: "schema-controls" },
    {
      kind: "paragraph",
      text: "A registry-driven calculator shows a design-method field only when its registry schema declares one. Today that field can expose explicit ASD and LRFD choices, but the schema workspace cannot clear the saved override back to Project default. The API model supports a null override; the matching UI control is missing. Because the display and persistence path are incomplete, this calculations section remains draft documentation rather than a publishable promise.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Do not infer inheritance from an empty help field",
      body: "The steel schema's design_method declaration has no help text, so no field HelpTip renders. Read the explicit choice and the method printed in the returned record. Do not describe that control as inheriting until the product provides a Project default option.",
    },

    { kind: "heading", level: 2, text: "Method is record provenance, not approval", id: "responsibility" },
    {
      kind: "paragraph",
      text: "The project default is a convenience for consistent routing, and an override is an intentional exception. Neither control selects the governing code edition, decides whether ASD or LRFD is permitted for the project, reconciles mixed-method deliverables, or approves a calculation. Those decisions remain with the responsible engineer and the project's adopted criteria.",
    },
    {
      kind: "paragraph",
      text: "For review, start with the record rather than the dropdown: confirm the effective method, standard and reference identity, governing combination, assumptions, warnings, and any linked-input state. If the sheet is OUT OF DATE, recompute it. If Latest recalculation failed is present, the visible record is the prior successful result, not evidence that the attempted method change succeeded.",
    },
    {
      kind: "related",
      topics: ["member-sheets", "schema-sheets", "sheet-lifecycle", "linking-reactions"],
    },
  ],
};
